import {
  Box,
  Button,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { PricingRule, SoleSellerSetting } from "../PricingSettings";
import { Controller, FieldValues } from "react-hook-form";
import { useEffect, useMemo } from "react";
import { useCustomMutation, useInvalidate } from "@refinedev/core";
import { useTranslation } from "../../../../hooks/useTranslation";
import LoadingButton from "@mui/lab/LoadingButton";
import { useForm } from "../../../../hooks/useForm";

type SoleSellerSettingProps = {
  defaultValues: PricingRule & SoleSellerSetting;
};

export const SoleSellerSettingRow = (props: SoleSellerSettingProps) => {
  const { defaultValues } = props;

  const soleSellerSetting = useMemo(
    () => ({
      useSoleSellerPercentage: defaultValues.useSoleSellerPercentage,
      additionalSoleSellerPercentage:
        defaultValues.additionalSoleSellerPercentage,
    }),
    [defaultValues],
  );

  const { mutate, isLoading } = useCustomMutation();
  const { translate, translateWithoutPrefix } = useTranslation(
    "pages.stores.pricingSettings.soleSeller",
  );
  const invalidate = useInvalidate();

  const {
    register,
    formState: { isDirty, errors },
    reset,
    control,
    handleSubmit,
    watch,
  } = useForm<SoleSellerSetting>({
    defaultValues: {
      useSoleSellerPercentage: defaultValues.useSoleSellerPercentage,
      additionalSoleSellerPercentage:
        defaultValues.additionalSoleSellerPercentage,
    },
  });

  useEffect(() => {
    reset(soleSellerSetting);
  }, [soleSellerSetting]);

  const onSubmit = (data: FieldValues) => {
    mutate(
      {
        url: `store/${defaultValues.marketplaceId}/price-settings`,
        method: "put",
        values: { ...defaultValues, ...data },
        successNotification: () => {
          return {
            message: translate("success"),
            type: "success",
          };
        },
      },
      {
        onSuccess: () => {
          invalidate({
            resource: "all",
            invalidates: ["all"],
          });

          reset(data);
        },
      },
    );
  };

  const useSoleSellerPercentage = watch("useSoleSellerPercentage");

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "16px",
        margin: "8px 0px",
        padding: "16px",
        borderRadius: "4px",
        backgroundColor: (theme) => theme.palette.customBackground.box,
      }}
    >
      <Typography variant="body1">{translate("description")}</Typography>
      <Controller
        name="useSoleSellerPercentage"
        control={control}
        render={({ field: { onChange, value } }) => (
          <Switch checked={value} onChange={onChange} />
        )}
      />

      {useSoleSellerPercentage && (
        <TextField
          type="number"
          label={translate("soleSellerProfit")}
          size="small"
          {...register("additionalSoleSellerPercentage", {
            required: translate("required"),
          })}
          error={!!errors.additionalSoleSellerPercentage}
          helperText={
            errors.additionalSoleSellerPercentage
              ? String(errors.additionalSoleSellerPercentage?.message)
              : ""
          }
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">%</InputAdornment>
              ),
            },
          }}
        />
      )}
      <Box flex="1" />
      <LoadingButton
        loading={isLoading}
        disabled={!isDirty}
        variant="contained"
        size="small"
        color="primary"
        onClick={handleSubmit(onSubmit)}
      >
        {translateWithoutPrefix("buttons.save")}
      </LoadingButton>
      <Button
        disabled={isLoading || !isDirty}
        variant="contained"
        color="error"
        size="small"
        onClick={() => reset(soleSellerSetting)}
      >
        {translateWithoutPrefix("buttons.cancel")}
      </Button>
    </Box>
  );
};
